import { btcTransactionSchema } from "@/utils/converters";
import { z } from "zod";

/**
 * Methods
 */
export const BIP122_SIGNING_METHODS = {
  BIP122_SIGN_MESSAGE_LEGACY: "bip122_signMessage",
  BIP122_SIGN_MESSAGE: "signMessage",
  BIP122_SEND_TRANSFERT: "sendTransfer",
  BIP122_SIGN_PSBT: "signPsbt",
} as const;

export const BIP122_QUERY_METHODS = {
  BIP122_GET_ACCOUNT_ADDRESSES: "getAccountAddresses",
} as const;

export const bip122SignMessageLegacySchema = z.strictObject({
  message: z.string(),
  address: z.string(),
});

export const bip122SignMessageSchema = z.strictObject({
  message: z.string(),
  account: z.string(),
  address: z.string().optional(),
  protocol: z
    .union([z.literal("ecdsa"), z.literal("bip322"), z.literal("")])
    .optional(),
});

export const bip122SignPsbtSchema = z.strictObject({
  account: z.string(),
  psbt: z.string(),
  signInputs: z
    .array(
      z.strictObject({
        address: z.string(),
        index: z.number().int(),
        sighashTypes: z.array(z.number().int().min(0).max(255)).optional(),
      }),
    )
    .optional(),
  broadcast: z.boolean().optional(),
});

export const bip122GetAccountAddressesSchema = z.strictObject({
  account: z.string(),
  intentions: z.array(z.string()).optional(),
});

/**
 * Requests specs : https://docs.reown.com/advanced/multichain/rpc-reference/bitcoin-rpc
 */
export type BIP122_REQUESTS =
  | {
      method: typeof BIP122_SIGNING_METHODS.BIP122_SIGN_MESSAGE_LEGACY;
      params: z.infer<typeof bip122SignMessageLegacySchema>;
    }
  | {
      method: typeof BIP122_SIGNING_METHODS.BIP122_SIGN_MESSAGE;
      params: z.infer<typeof bip122SignMessageSchema>;
    }
  | {
      method: typeof BIP122_SIGNING_METHODS.BIP122_SEND_TRANSFERT;
      params: z.infer<typeof btcTransactionSchema>;
    }
  | {
      method: typeof BIP122_SIGNING_METHODS.BIP122_SIGN_PSBT;
      params: z.infer<typeof bip122SignPsbtSchema>;
    }
  | {
      method: typeof BIP122_QUERY_METHODS.BIP122_GET_ACCOUNT_ADDRESSES;
      params: z.infer<typeof bip122GetAccountAddressesSchema>;
    };

export type BIP122AccountAddress = {
  address: string;
  publicKey?: string;
  path?: string;
  intention?: string;
};

/**
 * Responses specs : https://docs.reown.com/advanced/multichain/rpc-reference/bitcoin-rpc
 */
export type BIP122_RESPONSES = {
  [BIP122_SIGNING_METHODS.BIP122_SIGN_MESSAGE_LEGACY]: string;
  [BIP122_SIGNING_METHODS.BIP122_SIGN_MESSAGE]: {
    address: string;
    signature: string;
    messageHash?: string;
  };
  [BIP122_SIGNING_METHODS.BIP122_SEND_TRANSFERT]: {
    txid: string;
  };
  [BIP122_SIGNING_METHODS.BIP122_SIGN_PSBT]: {
    psbt: string;
    txid?: string;
  };
  [BIP122_QUERY_METHODS.BIP122_GET_ACCOUNT_ADDRESSES]: BIP122AccountAddress[];
};
